import { useState } from "react";
import Seo from "@/components/Seo";
import { JOBS, CAREERS_EMAIL, COMPANY } from "@/lib/constants";
import {
  ArrowRight,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle2,
  Send,
  Mail,
  Sparkles,
  TrendingUp,
  HeartHandshake,
  GraduationCap,
} from "lucide-react";

const BENEFITS = [
  { icon: TrendingUp, title: "Real career progression", body: "Structured pathway from trainee assessor through to senior licensed roles, with funded training." },
  { icon: GraduationCap, title: "Professional development", body: "We sponsor licensing, AIOH membership and ongoing CPD for every team member." },
  { icon: HeartHandshake, title: "Independent culture", body: "We're an asbestos consultancy — not a removalist. Every report reflects your honest assessment." },
  { icon: Sparkles, title: "Varied, meaningful work", body: "From CBD landmarks to emergency callouts, no two weeks look the same and the work genuinely matters." },
];

export default function Careers() {
  const [openJob, setOpenJob] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const applyTo = (jobTitle) => {
    setForm((f) => ({ ...f, position: jobTitle }));
    document
      .getElementById("apply-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Job Application — ${form.position || "General Enquiry"}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nPosition: ${form.position || "Open application"}\n\nMessage / cover note:\n${form.message}\n\n— Please attach your CV / resume to this email before sending. —`
    );
    window.location.href = `mailto:${CAREERS_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div data-testid="careers-page">
      <Seo
        title="Careers at ISGS Asbestos Consulting"
        description="Join ISGS Asbestos Consulting — open roles for licensed asbestos assessors, occupational hygienists, project coordinators and field technicians across Sydney and NSW."
        path="/careers"
      />

      <CareersHero />
      <CareersHiringBanner />
      <CareersBenefits />

      <section id="open-roles" className="section-pad bg-[#F7F8F3]">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4">Open roles</div>
              <h2 className="text-[34px] md:text-[44px] text-[#0E2A5C] leading-tight">
                Four ways to join the team.
              </h2>
            </div>
            <div className="text-[#5B6478] text-[14.5px] max-w-sm">
              Don't see your role? Send a speculative application — we're always interested in talented assessors and hygienists.
            </div>
          </div>

          <div className="space-y-4">
            {JOBS.map((j) => (
              <JobCard
                key={j.id}
                job={j}
                isOpen={openJob === j.id}
                onToggle={() => setOpenJob(openJob === j.id ? null : j.id)}
                onApply={() => applyTo(j.title)}
              />
            ))}
          </div>
        </div>
      </section>

      <CareersApplicationSection
        form={form}
        onChange={onChange}
        submitted={submitted}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

/* ─────────── HERO ─────────── */
function CareersHero() {
  return (
    <section className="bg-gradient-to-br from-[#FAFCF3] via-[#F0F6E4] to-white pt-20 pb-16 relative overflow-hidden border-b border-[#0E2A5C]/8">
      <div className="absolute -right-20 top-10 w-80 h-80 rounded-full bg-[#A6CD3C]/20 blur-3xl" />
      <div className="absolute -left-10 bottom-0 w-60 h-60 rounded-full bg-[#3B6CA8]/15 blur-3xl" />
      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 bg-white border border-[#0E2A5C]/15 rounded-full px-3.5 py-1 mb-6 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C] animate-pulse" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E2A5C]">
              We're hiring
            </span>
          </div>
          <h1 className="text-[44px] md:text-[64px] font-bold leading-[0.98] text-[#0E2A5C]">
            Build a career that <span className="relative inline-block"><span>matters</span><span className="absolute inset-x-0 bottom-1 h-3 bg-[#A6CD3C]/55 -z-10" /></span>.<br />
            <span className="italic font-light text-[#5B6478]">Help us protect Australian workplaces.</span>
          </h1>
          <p className="mt-6 text-[#0B0F1A]/70 text-[16.5px] max-w-2xl leading-relaxed">
            ISGS is growing. We're looking for licensed assessors, hygienists and motivated trainees who want to do consulting work that's genuinely independent — and genuinely impactful.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#open-roles" className="btn-primary" data-testid="careers-hero-view-roles">
              See open roles <ArrowRight size={18} />
            </a>
            <a href="#apply-form" className="btn-dark" data-testid="careers-hero-apply">
              <Mail size={17} /> Send your CV
            </a>
          </div>
        </div>
        <div className="lg:col-span-4 hidden lg:block">
          <div className="bg-white rounded-2xl p-6 shadow-[0_24px_50px_-22px_rgba(14,42,92,0.25)] border border-[#0E2A5C]/10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#A6CD3C] font-bold mb-4">
              Currently hiring
            </div>
            <div className="space-y-3">
              {JOBS.map((j) => (
                <a
                  key={j.id}
                  href="#open-roles"
                  className="block py-2.5 border-b border-[#0E2A5C]/8 last:border-0 hover:text-[#A6CD3C] group"
                >
                  <div className="text-[14.5px] font-semibold text-[#0E2A5C] group-hover:text-[#0A1F47] flex items-center justify-between">
                    {j.title}
                    <ArrowRight size={14} className="text-[#A6CD3C] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-[12px] text-[#5B6478] mt-0.5">{j.location} · {j.type}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── HIRING BANNER ─────────── */
function CareersHiringBanner() {
  return (
    <section className="bg-[#F7F8F3] py-14 border-y border-[#0E2A5C]/8" data-testid="careers-hiring-banner">
      <div className="container-x">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C] animate-pulse" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#0E2A5C]">We're hiring</span>
            </div>
            <h3 className="text-[24px] md:text-[30px] font-bold text-[#0E2A5C] leading-tight">
              Grow your career in asbestos consulting.
            </h3>
            <p className="text-[#5B6478] mt-2 text-[14.5px]">
              Open roles for licensed assessors, hygienists, project coordinators and trainees.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#open-roles" className="btn-primary" data-testid="careers-banner-view-roles">
              View open roles <ArrowRight size={17} />
            </a>
            <a href="#apply-form" className="btn-dark" data-testid="careers-banner-send-cv">
              Send your CV
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── BENEFITS GRID ─────────── */
function CareersBenefits() {
  return (
    <section className="section-pad bg-white">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-4">Why ISGS</div>
          <h2 className="text-[34px] md:text-[44px] text-[#0E2A5C] leading-tight">
            Independent consulting work — without the corporate grind.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b) => (
            <div
              key={b.title}
              className="bg-white rounded-2xl p-7 border border-[#0E2A5C]/10 hover:border-[#A6CD3C] transition-colors"
              data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#0E2A5C] text-[#A6CD3C] flex items-center justify-center mb-5">
                <b.icon size={22} />
              </div>
              <h3 className="text-[17px] font-semibold text-[#0E2A5C] mb-2">{b.title}</h3>
              <p className="text-[13.5px] text-[#5B6478] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── JOB CARD (collapsible) ─────────── */
function JobCard({ job, isOpen, onToggle, onApply }) {
  return (
    <div
      className="bg-white rounded-2xl border border-[#0E2A5C]/10 overflow-hidden transition-all hover:border-[#A6CD3C]"
      data-testid={`job-${job.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 group"
        data-testid={`job-toggle-${job.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#A6CD3C]">
              {job.department}
            </span>
          </div>
          <h3 className="text-[20px] md:text-[22px] font-semibold text-[#0E2A5C] mb-2">
            {job.title}
          </h3>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13.5px] text-[#5B6478]">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-[#3B6CA8]" /> {job.location}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} className="text-[#3B6CA8]" /> {job.type}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Briefcase size={14} className="text-[#3B6CA8]" /> Sydney
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            onClick={(e) => { e.stopPropagation(); onApply(); }}
            role="button"
            tabIndex={0}
            className="btn-primary !py-2.5 !px-5 text-[13px]"
            data-testid={`job-apply-${job.id}`}
          >
            Apply <ArrowRight size={15} />
          </span>
          <span
            className={`text-[#0E2A5C] transition-transform ${isOpen ? "rotate-45" : ""}`}
            aria-hidden="true"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="px-6 md:px-7 pb-7 border-t border-[#0E2A5C]/8 pt-6" data-testid={`job-detail-${job.id}`}>
          <p className="text-[#0B0F1A]/80 text-[15px] leading-relaxed mb-5">
            {job.summary}
          </p>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0E2A5C] mb-3">
            Key requirements
          </div>
          <ul className="space-y-2">
            {job.requirements.map((r) => (
              <li key={r} className="flex items-start gap-2.5 text-[14.5px] text-[#0B0F1A]/80">
                <CheckCircle2 size={17} className="text-[#A6CD3C] mt-0.5 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ─────────── APPLICATION FORM SECTION ─────────── */
function CareersApplicationSection({ form, onChange, submitted, onSubmit }) {
  return (
    <section id="apply-form" className="section-pad bg-white">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Apply now</div>
          <h2 className="text-[32px] md:text-[42px] text-[#0E2A5C] leading-tight mb-6">
            Send your CV — we read every application.
          </h2>
          <p className="text-[#5B6478] text-[15px] leading-relaxed mb-7">
            Fill in the form and we'll open your email client with a prefilled message. <strong className="text-[#0E2A5C]">Attach your CV / resume</strong> before sending.
          </p>

          <div className="bg-[#F7F8F3] border border-[#0E2A5C]/10 rounded-2xl p-6 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B6478] font-bold mb-1.5">
                Email applications
              </div>
              <a
                href={`mailto:${CAREERS_EMAIL}?subject=Job%20Application`}
                className="text-[#0E2A5C] font-semibold hover:text-[#A6CD3C] inline-flex items-center gap-2"
                data-testid="careers-direct-email"
              >
                <Mail size={16} /> {CAREERS_EMAIL}
              </a>
            </div>
            <div className="border-t border-[#0E2A5C]/8 pt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B6478] font-bold mb-1.5">
                Or call us
              </div>
              <a
                href={`tel:${COMPANY.phoneAltTel}`}
                className="text-[#0E2A5C] font-semibold hover:text-[#A6CD3C]"
              >
                {COMPANY.phoneAlt}
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-[#F7F8F3] rounded-3xl p-8 md:p-10 border border-[#0E2A5C]/10">
            {submitted ? (
              <div className="bg-[#A6CD3C]/15 border border-[#A6CD3C] rounded-2xl p-8 text-center" data-testid="careers-success">
                <CheckCircle2 size={42} className="text-[#A6CD3C] mx-auto mb-3" />
                <h3 className="text-[20px] font-semibold text-[#0E2A5C] mb-2">Email client opened</h3>
                <p className="text-[#5B6478] text-[14.5px]">
                  Your application has been prepared. <strong>Attach your CV</strong> and send it through to {CAREERS_EMAIL} to complete your application.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="careers-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
                  <Field label="Email address" name="email" type="email" value={form.email} onChange={onChange} required />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                  <div>
                    <label className="block text-[12.5px] uppercase tracking-[0.18em] text-[#0E2A5C] font-semibold mb-2">
                      Position
                    </label>
                    <select
                      name="position"
                      value={form.position}
                      onChange={onChange}
                      className="w-full bg-white border border-[#0E2A5C]/15 rounded-xl px-4 py-3 text-[15px] text-[#0B0F1A] focus:outline-none focus:border-[#A6CD3C] focus:ring-2 focus:ring-[#A6CD3C]/30 transition-all"
                      data-testid="careers-position-select"
                    >
                      <option value="">Open application</option>
                      {JOBS.map((j) => (
                        <option key={j.id} value={j.title}>{j.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[12.5px] uppercase tracking-[0.18em] text-[#0E2A5C] font-semibold mb-2">
                    Cover note
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell us about your experience and why you'd like to join ISGS..."
                    className="w-full bg-white border border-[#0E2A5C]/15 rounded-xl px-4 py-3 text-[15px] text-[#0B0F1A] focus:outline-none focus:border-[#A6CD3C] focus:ring-2 focus:ring-[#A6CD3C]/30 transition-all resize-none"
                    data-testid="careers-message-input"
                  />
                </div>
                <div className="bg-[#3B6CA8]/10 border border-[#3B6CA8]/25 rounded-xl p-4 text-[13px] text-[#0E2A5C] flex items-start gap-3">
                  <Mail size={18} className="text-[#3B6CA8] shrink-0 mt-0.5" />
                  <div>
                    Submitting this form opens your email app pre-filled — <strong>please attach your CV/resume</strong> before sending to <strong>{CAREERS_EMAIL}</strong>.
                  </div>
                </div>
                <button type="submit" className="btn-primary w-full justify-center !py-4 text-[15px]" data-testid="careers-submit-btn">
                  Submit Application <Send size={17} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="block text-[12.5px] uppercase tracking-[0.18em] text-[#0E2A5C] font-semibold mb-2">
        {label}
        {required && <span className="text-[#A6CD3C]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-white border border-[#0E2A5C]/15 rounded-xl px-4 py-3 text-[15px] text-[#0B0F1A] focus:outline-none focus:border-[#A6CD3C] focus:ring-2 focus:ring-[#A6CD3C]/30 transition-all"
        data-testid={`careers-${name}-input`}
      />
    </div>
  );
}
